import React from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Link, Chip } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { styled } from '@mui/system';
import CardContainer from '../shared/CardContainer';
import BaseImage from '../shared/BaseImage';
import Slider from 'react-slick';

const ProjectCard = styled(CardContainer)(({ theme }) => ({
  flex: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2.5),
  margin: 0,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.06)',
  overflow: 'hidden',
}));

const MediaArea = styled(Box)({
  position: 'relative',
  width: '100%',
  height: 200,
  borderRadius: 8,
  overflow: 'hidden',
  backgroundColor: '#f3f4f6',
  marginBottom: 8,
  '& .slick-slider': {
    height: '100%',
    marginBottom: '0 !important',
  },
  '& .slick-dotted.slick-slider': {
    marginBottom: '0 !important',
  },
  '& .slick-list, & .slick-track, & .slick-slide, & .slick-slide > div': {
    height: '100%',
  },
  '& .slick-dots': {
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0,
    margin: 0,
    padding: 0,
    width: '100%',
    height: 'auto',
    listStyle: 'none',
    textAlign: 'center',
    zIndex: 2,
  },
  '& .slick-dots li': {
    margin: '0 3px',
  },
  '& .slick-dots li button:before': {
    fontSize: 8,
    color: '#fff',
    opacity: 0.55,
    textShadow: '0 1px 2px rgba(0,0,0,0.45)',
  },
  '& .slick-dots li.slick-active button:before': {
    opacity: 1,
    color: '#fff',
  },
});

const ProjectImageWrapper = styled(Box)({
  position: 'relative',
  cursor: 'pointer',
  height: '100%',
  overflow: 'hidden',
  '&:hover .overlay-text': {
    opacity: 1,
  },
  '&:hover img': {
    filter: 'brightness(0.45)',
  },
});

const ProjectImage = styled(BaseImage)({
  height: '100%',
  borderRadius: 0,
  transition: 'filter 0.3s ease',
});

const OverlayText = styled(Typography)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  color: 'white',
  fontSize: '1.05rem',
  fontWeight: 600,
  opacity: 0,
  transition: 'opacity 0.3s ease',
  zIndex: 1,
  pointerEvents: 'none',
  textAlign: 'center',
  padding: '0 12px',
  width: '90%',
});

const FlatAccordion = styled(Accordion)({
  boxShadow: 'none',
  backgroundImage: 'none',
  backgroundColor: 'transparent',
  borderRadius: '0 !important',
  margin: '0 !important',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 !important',
  },
});

const FlatAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  minHeight: 40,
  padding: 0,
  borderTop: '1px solid rgba(0,0,0,0.08)',
  '& .MuiAccordionSummary-content': {
    margin: `${theme.spacing(1)} 0`,
  },
  '&.Mui-expanded': {
    minHeight: 40,
  },
}));

const FlatAccordionDetails = styled(AccordionDetails)({
  padding: '0 0 12px',
});

const Project = ({ data }) => {
  const { name, description, images, url, stack, gitUrl } = data;
  const hasCarousel = images.length > 1;

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
  };

  const renderImage = (image, index) => (
    <ProjectImageWrapper key={index} onClick={() => window.open(url, '_blank')}>
      <ProjectImage src={image} alt={`${name} - ${index + 1}`} />
      <OverlayText className="overlay-text">{description}</OverlayText>
    </ProjectImageWrapper>
  );

  return (
    <ProjectCard elevation={0}>
      <Box mb={1.5}>
        <Link
          href={url}
          target="_blank"
          underline="hover"
          variant="h6"
          color="primary"
          sx={{ fontWeight: 600, display: 'inline-block' }}
        >
          {name}
        </Link>
        {gitUrl && (
          <Box mt={0.5}>
            <Link href={gitUrl} target="_blank" underline="hover" variant="body2">
              source code
            </Link>
          </Box>
        )}
      </Box>

      <MediaArea>
        {hasCarousel ? (
          <Slider {...settings}>{images.map(renderImage)}</Slider>
        ) : (
          renderImage(images[0], 0)
        )}
      </MediaArea>

      <Box mt="auto">
        <FlatAccordion disableGutters elevation={0}>
          <FlatAccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
            <Typography variant="body2" color="text.secondary">
              Description
            </Typography>
          </FlatAccordionSummary>
          <FlatAccordionDetails>
            <Typography variant="body2">{description}</Typography>
          </FlatAccordionDetails>
        </FlatAccordion>

        <FlatAccordion disableGutters elevation={0}>
          <FlatAccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
            <Typography variant="body2" color="text.secondary">
              Stack
            </Typography>
          </FlatAccordionSummary>
          <FlatAccordionDetails>
            <Box display="flex" flexWrap="wrap" gap={0.75}>
              {stack.map((tech, index) => (
                <Chip key={index} label={tech} size="small" variant="outlined" />
              ))}
            </Box>
          </FlatAccordionDetails>
        </FlatAccordion>
      </Box>
    </ProjectCard>
  );
};

export default Project;
